import { useQuery } from "@tanstack/react-query";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";

import { INVOICE_DETAILS_PROJECT_ID_PARAM_NAME, INVOICES_PENDING_PATH } from "~/constants/appRoutes.ts";
import { useAuth } from "~/contexts/AuthContext";
import userViewGuard from "~/hooks/useViewPermssionsCheck";
import { invoiceStatusId } from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import { invoiceStatusQueries } from "~/modules/billing/api/invoiceStatus/invoiceStatusQueries.ts";
import { processedInvoiceQueries } from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import type {
	ValidInvoiceDetailsNavigatedFromOrigin,
} from "~/modules/billing/components/InvoiceDetailsView/invoiceDetailsUtils.ts";
import {
	validInvoiceDetailsNavigatedFromOrigins,
} from "~/modules/billing/components/InvoiceDetailsView/invoiceDetailsUtils.ts";
import InvoiceDetailsView from "~/modules/billing/components/InvoiceDetailsView/InvoiceDetailsView";
import { useProject } from "~/modules/project/api/project/projectQueries.ts";
import { quotaQueries } from "~/modules/quota/api/quota/quotaQueries.ts";
import type { QuotaWithRelations } from "~/modules/quota/api/quota/quotaTypes.ts";
import { QUOTA_ACCESS_PERMISSIONS } from "~/modules/quota/utils/quotaPermissionUtils.ts";
import { useAllUsers } from "~/modules/user/api/user/userQueries.ts";
import LoadingPage from "~/pages/LoadingPage.tsx";
import { PermissionNames } from "~/types/entityNames.ts";


const InvoiceDetailsPage: FunctionComponent = () => {
	const guard = userViewGuard([PermissionNames.CanManageInvoices, PermissionNames.CanViewInvoices, PermissionNames.CanManageProjects]);
	if (guard) return guard;

	const [searchParams] = useSearchParams();
	const fromParam = searchParams.get("from") as ValidInvoiceDetailsNavigatedFromOrigin;
	const projectIdParam = searchParams.get(INVOICE_DETAILS_PROJECT_ID_PARAM_NAME);
	const { invoiceId } = useParams();
	const { hasAnyPermission } = useAuth();
	// preload all users
	useAllUsers();

	const {
		data: invoiceStatusData,
		isLoading: invoiceStatusIsLoading,
	} = useQuery(invoiceStatusQueries.all());

	const {
		data: invoiceData,
		isLoading: invoiceIsLoading,
	} = useQuery(processedInvoiceQueries.details(invoiceId!));

	const quota = invoiceData ? invoiceData.quota : undefined;
	// A user with invoice access but without quota access still opens the invoice. The view then shows
	// the quota from the invoice data only, because the quota route refuses that user.
	const userCanAccessQuotas = hasAnyPermission(QUOTA_ACCESS_PERMISSIONS);
	const loadsQuota = !!quota && userCanAccessQuotas;

	const {
		data: quotaDataWithRelations,
		isLoading: quotaIsLoading,
	} = useQuery({
		...quotaQueries.details(quota?.companyId || "", quota?.id || "", true),
		enabled: loadsQuota,
	});

	const navigatedFromSanitised = useMemo(() => {
		if (fromParam) {
			if (Object.values(validInvoiceDetailsNavigatedFromOrigins).includes(fromParam)) {
				return fromParam;
			}
		}
		return undefined;
	}, [fromParam]);

	// The revenue origin returns to the project it names. Without one, it returns to the invoice's own project.
	const navigatedFromProjectId = navigatedFromSanitised === validInvoiceDetailsNavigatedFromOrigins.PROJECT_REVENUE_REPORT
		? projectIdParam ?? undefined
		: undefined;

	const {
		data: navigatedFromProjectData,
		isLoading: navigatedFromProjectIsLoading,
	} = useProject(navigatedFromProjectId ?? "");

	const isLoading = invoiceIsLoading || quotaIsLoading || invoiceStatusIsLoading || navigatedFromProjectIsLoading;
	if (isLoading) return <LoadingPage />;

	if (invoiceData === undefined || loadsQuota && !quotaDataWithRelations || invoiceStatusData === undefined) {
		return null;
	}

	if (invoiceData.invoiceStatusId === invoiceStatusId.Pending) {
		return <Navigate to={INVOICES_PENDING_PATH} />;
	}

	return <InvoiceDetailsView invoiceData={invoiceData}
							   invoiceStatuses={invoiceStatusData}
							   navigatedFrom={navigatedFromSanitised}
							   navigatedFromProject={navigatedFromProjectData}
							   quota={quotaDataWithRelations as QuotaWithRelations | undefined}
	/>;
};

export default InvoiceDetailsPage;